import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import FilterRowScrollPanelCss from "./FilterRowScrollPanel.module.css";
import ChevronLeftIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronLeftIconPath";
import ChevronRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronRightIconPath";
import { useTranslation } from "@coworker/locales";
import FixaButton from "../../wrappers/FixaButton/FixaButton";

export const FilterRowScrollPanel = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);

  // Memoize the function in a useCallback hook so it doesn't change on every render
  const updateOverflows = useCallback(() => {
    if (ref && ref.current) {
      setHasOverflowLeft(ref.current.scrollLeft > 2);
      setHasOverflowRight(
        ref.current.scrollWidth -
          ref.current.clientWidth -
          ref.current.scrollLeft >
          2
      );
    }
  }, []);

  // Listen for window resize and trigger updateOverflows
  useEffect(() => {
    window.addEventListener("resize", updateOverflows);
    return () => window.removeEventListener("resize", updateOverflows);
  }, [updateOverflows]);

  // Listen for scrollWidth change div with the children, the buttons/pill filter row.
  useEffect(() => {
    if (ref && ref.current) {
      const observer = new ResizeObserver(() => {
        if (ref.current) {
          updateOverflows();
        }
      });
      observer.observe(ref.current);

      return () => observer.disconnect();
    }
    return;
  }, [updateOverflows]);

  const scroll = (direction: "left" | "right") => {
    if (
      ref &&
      ref.current &&
      ref.current.scroll &&
      ref.current.scrollLeft !== undefined &&
      ref.current.clientWidth !== undefined
    ) {
      const { scrollLeft, clientWidth } = ref.current;
      ref.current.scroll({
        left:
          scrollLeft + (direction === "right" ? clientWidth : -clientWidth) / 2,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className={FilterRowScrollPanelCss["filters-container"]}>
      <div
        ref={ref}
        onScroll={updateOverflows}
        className={FilterRowScrollPanelCss["filters-row-container"]}
      >
        {children}
      </div>
      {hasOverflowLeft && (
        <div
          className={FilterRowScrollPanelCss["filters-row-overlay-arrow-left"]}
        >
          <FixaButton
            ssrIcon={ChevronLeftIconPath}
            iconOnly
            type="tertiary"
            size={"small"}
            onClick={() => scroll("left")}
            aria-label={t("scrollLeftString")}
            style={{ background: "var(--white)" }}
          />
        </div>
      )}
      {hasOverflowRight && (
        <div
          className={FilterRowScrollPanelCss["filters-row-overlay-arrow-right"]}
        >
          <FixaButton
            ssrIcon={ChevronRightIconPath}
            iconOnly
            type="tertiary"
            size={"small"}
            onClick={() => scroll("right")}
            aria-label={t("scrollRightString")}
            style={{ background: "var(--white)" }}
          />
        </div>
      )}
    </div>
  );
};
